import { useEffect, useState } from "react";
import {
  getMyReferralReward,
  MyReferralReward,
} from "apis/referralServices";

export interface UseReferralRewardResult {
  reward: MyReferralReward | null;
  loading: boolean;
  /** true when the endpoint 404s → the referral program is not active. */
  notActive: boolean;
  /** true for any non-404 failure (network/5xx). */
  error: boolean;
}

/**
 * Loads the current user's referral reward amounts.
 *
 * Pass `enabled` (e.g. the modal's `isOpen`) so the request only fires when the
 * data is actually needed, and re-fetches when the modal is reopened. Cleans up
 * on unmount / when disabled to avoid setting state after unmount.
 */
export function useReferralReward(enabled: boolean): UseReferralRewardResult {
  const [reward, setReward] = useState<MyReferralReward | null>(null);
  const [loading, setLoading] = useState(false);
  const [notActive, setNotActive] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(false);
    setNotActive(false);

    getMyReferralReward()
      .then((data) => {
        if (!cancelled) setReward(data);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotActive(true);
        } else {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { reward, loading, notActive, error };
}
